// src/layouts/reportes/index.js
import { useEffect, useMemo, useState, useCallback, useRef } from "react";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import esLocale from "date-fns/locale/es";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import SearchIcon from "@mui/icons-material/Search";
import TablePagination from "@mui/material/TablePagination";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import api from "services/api";
import { useAuth } from "layouts/auth/AuthContext";

// catálogos que ya usas en certificaciones (para mapas id->nombre)
import {
  listFormatos,
  listPerformers,
  listSellos,
  listGrupos,
} from "../certificaciones/services/certificaciones";

/* ----------------- utils ----------------- */
const isAdminUser = (u) =>
  u?.is_admin === true || u?.is_admin === 1 || String(u?.is_admin) === "1";

// Normaliza listas que pueden venir en data/page/results
const unwrapList = (data) =>
  Array.isArray(data) ? data
  : Array.isArray(data?.data) ? data.data
  : Array.isArray(data?.page) ? data.page
  : Array.isArray(data?.results) ? data.results
  : [];

// Pide una página con búsqueda opcional
async function fetchPagedOptions({
  endpoint,
  q = "",
  page = 0,
  pageSize = 20,
  nameKey = "name",
  extraQuery = {},
}) {
  const params = { __limit: pageSize, __offset: page * pageSize, ...(extraQuery || {}) };
  if (q.trim()) params[`${nameKey}[$contains]`] = q.trim();

  const { data } = await api.get(endpoint, { params });
  const list = unwrapList(data);

  return (list || [])
    .map((x) => ({
      id: String(x.id ?? x._id ?? ""),
      name: x[nameKey] ?? x.nombre ?? x.title ?? `#${x.id ?? x._id ?? ""}`,
    }))
    .filter((o) => o.id);
}


/** Hook genérico para Autocomplete paginado + búsqueda */
function usePagedAutocomplete({
  endpoint,
  nameKey = "name",
  firstPageSize = 10,
  pageSize = 20,
  debounceMs = 300,
  extraQuery = {},
}) {
  const [option, setOption] = useState(null);
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  // Carga inicial
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const first = await fetchPagedOptions({ endpoint, page: 0, pageSize: firstPageSize, nameKey, extraQuery });
        if (cancel) return;
        setOptions(first);
        setPage(0);
        setHasMore(first.length >= firstPageSize);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [endpoint, nameKey, firstPageSize, JSON.stringify(extraQuery || {})]);

  // Búsqueda con debounce
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const first = await fetchPagedOptions({ endpoint, q: input, page: 0, pageSize, nameKey, extraQuery });
        setOptions(first);
        setPage(0);
        setHasMore(first.length >= pageSize);
      } finally {
        setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timerRef.current);
  }, [input, endpoint, pageSize, nameKey, debounceMs, JSON.stringify(extraQuery || {})]);

  const loadMore = async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      const nextPage = page + 1;
      const next = await fetchPagedOptions({ endpoint, q: input, page: nextPage, pageSize, nameKey, extraQuery });
      setOptions((prev) => [...prev, ...next]);
      setPage(nextPage);
      setHasMore(next.length >= pageSize);
    } finally {
      setLoading(false);
    }
  };

  const listboxProps = {
    onScroll: (e) => {
      const n = e.currentTarget;
      if (n.scrollTop + n.clientHeight >= n.scrollHeight - 8) loadMore();
    },
    style: { maxHeight: 320, overflow: "auto" },
  };

  return { option, setOption, input, setInput, options, loading, listboxProps };
}

// ===== Datos paginados server-side (tabla) =====
async function fetchAllCertifiedWithFilters(params) {
  const pageSize = 1000;
  let all = [];
  let offset = 0;
  let total = Infinity;

  // 1) __limit/__offset
  while (offset < total) {
    const { data } = await api.get("/certified", {
      params: { __limit: pageSize, __offset: offset, ...params },
    });
    const page = unwrapList(data);
    const curTotal = Number(data?.total ?? data?.count ?? page.length);
    if (Number.isFinite(curTotal)) total = curTotal;

    all = all.concat(page);
    if (!page.length || page.length < pageSize) break;
    offset += page.length;
  }

  // 2) fallback limit/offset
  if (!all.length) {
    offset = 0; total = Infinity;
    while (offset < total) {
      const { data } = await api.get("/certified", {
        params: { limit: pageSize, offset, ...params },
      });
      const page = unwrapList(data);
      const curTotal = Number(data?.total ?? data?.count ?? page.length);
      if (Number.isFinite(curTotal)) total = curTotal;

      all = all.concat(page);
      if (!page.length || page.length < pageSize) break;
      offset += page.length;
    }
  }

  // 3) sin paginación
  if (!all.length) {
    const { data } = await api.get("/certified", { params });
    all = unwrapList(data);
  }
  return all;
}

const toYMD = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
};

const safeDate = (v) => {
  if (!v) return "";
  try {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d.toLocaleDateString() : String(v);
  } catch {
    return String(v);
  }
};

const formatUnits = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const raw = String(v).replace(/,/g, "").trim();
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-US");
};

const valueOrName = (v) => {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.map(valueOrName).filter(Boolean).join(", ");
  if (typeof v === "object") return v.name ?? v.title ?? v.award ?? v.value ?? v.id ?? "";
  return String(v);
};

const mapById = (arr = []) =>
  (arr || []).reduce((acc, it) => {
    const id = String(it.id ?? it._id ?? "");
    acc[id] = it;
    return acc;
  }, {});

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

// ================= Excel (.xlsx) =================
async function exportXLSX({ filename, cols, rows }) {
  if (!Array.isArray(rows)) {
    throw new TypeError("rows must be an array");
  }
  const XLSX = await import("xlsx");

  const header = cols.map((c) => c.label);
  const keys   = cols.map((c) => c.key);

  const dataAoA = [
    header,
    ...rows.map((r) => keys.map((k) => r[k] ?? "")),
  ];

  const ws = XLSX.utils.aoa_to_sheet(dataAoA);
  const range = XLSX.utils.decode_range(ws["!ref"]);
  ws["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
  ws["!cols"] = cols.map((c) => ({ wpx: c.w || 120 }));
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft", state: "frozen" };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Reporte");
  XLSX.writeFile(wb, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

/* ----- Definición de columnas (con anchos fijos) ----- */
const COLS = [
  { key: "artist",      label: "Artista",       w: 180 },
  { key: "title",       label: "Título",        w: 200 },
  { key: "album",       label: "Álbum",         w: 180 },
  { key: "releaseDate", label: "Release Date",  w: 120 },
  { key: "isrc",        label: "ISRC",          w: 130 },
  { key: "award",       label: "Award",         w: 140 },
  { key: "nationality", label: "Nac.",          w: 60  },
  { key: "units",       label: "Unidades",      w: 110 },
  { key: "format",      label: "Formato",       w: 160 },
  { key: "performer",   label: "Intérprete",    w: 180 },
  { key: "label",       label: "Sello",         w: 170 },
  { key: "group",       label: "Grupo",         w: 160 },
  { key: "public",      label: "Público",       w: 80  },
  { key: "genres",      label: "Géneros",       w: 220 },
  { key: "recordType",  label: "Grabación",     w: 220 },
  { key: "created",     label: "Creado",        w: 120 },
];

const TABLE_MIN_WIDTH = COLS.reduce((s, c) => s + c.w, 0);

/* ================== COMPONENTE ================== */
export default function Reportes() {
  // paginación server-side
  const [page, setPage] = useState(0);          // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const { user } = useAuth();
  const admin = isAdminUser(user);

  // rango rápido + fechas
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  // autocompletes (texto)
  const [artist, setArtist] = useState("");
  const [title, setTitle] = useState("");
  const [album, setAlbum] = useState("");

  // selects (ids usados en buildQuery)
  const [formatId, setFormatId] = useState("");
  const [performerId, setPerformerId] = useState("");
  const [recordLabelId, setRecordLabelId] = useState("");
  const [groupId, setGroupId] = useState("");

  const enforcedGroupQuery = {};

  // === DROPLISTS con hook genérico ===
  const formatAC   = usePagedAutocomplete({ endpoint: "/format",      nameKey: "name" });
  const perfAC     = usePagedAutocomplete({ endpoint: "/performer",   nameKey: "name" });
  const labelAC    = usePagedAutocomplete({ endpoint: "/recordLabel", nameKey: "name", extraQuery: enforcedGroupQuery, });
  const groupAC    = usePagedAutocomplete({ endpoint: "/group",       nameKey: "name" });

  // sincr. IDs seleccionados con estado usado en buildQuery
  useEffect(() => { setFormatId(formatAC.option?.id || ""); },   [formatAC.option]);
  useEffect(() => { setPerformerId(perfAC.option?.id || ""); },  [perfAC.option]);
  useEffect(() => { setRecordLabelId(labelAC.option?.id || ""); },[labelAC.option]);
  useEffect(() => { setGroupId(groupAC.option?.id || ""); }, [groupAC.option]);

  // catálogos para mapear id -> nombre al mostrar/exportar
  const [optsFormatos, setOptsFormatos] = useState([]);
  const [optsPerformers, setOptsPerformers] = useState([]);
  const [optsSellos, setOptsSellos] = useState([]);
  const [optsGrupos, setOptsGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // datos tabla paginados
  const [rows, setRows] = useState([]);

  // carga catálogos para mapeo
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [f, p, s, g] = await Promise.all([
          listFormatos(),
          listPerformers(),
          listSellos(),
          listGrupos(),
        ]);
        if (cancel) return;
        setOptsFormatos(f || []);
        setOptsPerformers(p || []);
        setOptsSellos(s || []);
        setOptsGrupos(g || []);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.message || e.message || String(e));
      }
    })();
    return () => { cancel = true; };
  }, [admin, user]);

  // lookups para nombres en tabla/export
  const formatosById   = useMemo(() => mapById(optsFormatos), [optsFormatos]);
  const performersById = useMemo(() => mapById(optsPerformers), [optsPerformers]);
  const sellosById     = useMemo(() => mapById(optsSellos), [optsSellos]);
  const gruposById     = useMemo(() => mapById(optsGrupos), [optsGrupos]);

  // ===== Autocomplete de texto libre (artist/title/album) =====
  function buildSuggestQuery(field, term, userObj) {
    const parts = [`${field}[$contains]=${encodeURIComponent(term)}`];
    if (!isAdminUser(userObj) && userObj?.group_id != null) {
      parts.push(`group_id[$eq]=${encodeURIComponent(String(userObj.group_id))}`);
    }
    return parts.join("&");
  }
  async function fetchSuggest(field, text, setLoadingState, setOptionsState, userObj) {
    const term = (text || "").trim();
    if (term.length < 2) {
      setOptionsState([]);
      setLoadingState(false);
      return;
    }
    setLoadingState(true);
    try {
      const qs = buildSuggestQuery(field, term, userObj);
      const { data } = await api.get(`/certified?${qs}`);
      const list = unwrapList(data);
      const values = Array.from(new Set(list.map((r) => String(r?.[field] || "")).filter(Boolean)));
      setOptionsState(values);
    } catch {
      setOptionsState([]);
    } finally {
      setLoadingState(false);
    }
  }
  const [optArtist, setOptArtist] = useState([]);
  const [optTitle, setOptTitle]   = useState([]);
  const [optAlbum, setOptAlbum]   = useState([]);
  const [loadingArtist, setLoadingArtist] = useState(false);
  const [loadingTitle, setLoadingTitle]   = useState(false);
  const [loadingAlbum, setLoadingAlbum]   = useState(false);

  const makeDebouncedSuggest = (setLoadingState, setOptionsState, field, userObj) => {
    let t = null;
    return (text) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => fetchSuggest(field, text, setLoadingState, setOptionsState, userObj), 300);
    };
  };

  const debouncedArtist = useMemo(
    () => makeDebouncedSuggest(setLoadingArtist, setOptArtist, "artist", user),
    [user]
  );
  const debouncedTitle = useMemo(
    () => makeDebouncedSuggest(setLoadingTitle, setOptTitle, "title", user),
    [user]
  );
  const debouncedAlbum = useMemo(
    () => makeDebouncedSuggest(setLoadingAlbum, setOptAlbum, "album", user),
    [user]
  );

  // rango rápido → fechas
  const setQuickRange = (key) => {
    setRange(key);
    if (key === "all") { setFrom(null); setTo(null); return; }
    const today = new Date();
    if (key === "1m")  { setFrom(addMonths(today, -1));  setTo(today); }
    if (key === "6m")  { setFrom(addMonths(today, -6));  setTo(today); }
    if (key === "1y")  { setFrom(addMonths(today, -12)); setTo(today); }
  };

  // construir query (fechas por created_at)
  const buildQuery = () => {
    const parts = [];
    if (artist.trim()) parts.push(`artist[$contains]=${encodeURIComponent(artist.trim())}`);
    if (title.trim())  parts.push(`title[$contains]=${encodeURIComponent(title.trim())}`);
    if (album.trim())  parts.push(`album[$contains]=${encodeURIComponent(album.trim())}`);

    const f = toYMD(from);
    const t = toYMD(to);
    if (f) parts.push(`created_at[$gte]=${encodeURIComponent(f)}`);
    if (t) parts.push(`created_at[$lte]=${encodeURIComponent(t)}`);

    if (formatId)      parts.push(`format_id[$eq]=${encodeURIComponent(String(formatId))}`);
    if (performerId)   parts.push(`performer_id[$eq]=${encodeURIComponent(String(performerId))}`);
    if (recordLabelId) parts.push(`record_label_id[$eq]=${encodeURIComponent(String(recordLabelId))}`);

    if (groupId) {
      parts.push(`group_id[$eq]=${encodeURIComponent(String(groupId))}`);
    }
    return parts.join("&");
  };

  const fetchRows = useCallback(async ({ pageIndex = page, pageSize = rowsPerPage } = {}) => {
    setLoading(true);
    setErr("");
    try {
      const qs = buildQuery();
      const params = new URLSearchParams(qs);
      params.set("__limit", String(pageSize));
      params.set("__offset", String(pageIndex * pageSize));

      const { data } = await api.get(`/certified?${params.toString()}`);
      const list = unwrapList(data);

      setRows(list);
      setTotal(Number(data?.total ?? data?.count ?? list.length));
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Error al consultar");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, artist, title, album, from, to, formatId, performerId, recordLabelId, groupId, admin, user]);

  // carga inicial
  useEffect(() => {
    fetchRows({ pageIndex: 0, pageSize: rowsPerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    setRange("all");
    setFrom(null);
    setTo(null);
    setArtist("");
    setTitle("");
    setAlbum("");
    setFormatId("");
    setPerformerId("");
    setRecordLabelId("");
    setGroupId("");
    groupAC.setOption(null);
    groupAC.setInput("");
    setRows([]);
  };

  // transformar filas a las columnas visibles (alineadas)
  const tableRows = useMemo(() => {
    return (rows || []).map((r) => {
      const fid = String(r.format_id ?? "");
      const pid = String(r.performer_id ?? "");
      const lid = String(r.record_label_id ?? "");
      const gid = String(r.group_id ?? "");
      return {
        artist:      r.artist ?? "",
        title:       r.title ?? "",
        album:       r.album ?? "",
        releaseDate: safeDate(r.release_date),
        isrc:        r.isrc ?? "",
        award:       valueOrName(r.award),
        nationality: r.nationality ?? "",
        units:       formatUnits(r.sale_units),
        format:      formatosById[fid]?.name ?? r.format?.name ?? fid,
        performer:   performersById[pid]?.name ?? r.performer?.name ?? pid,
        label:       sellosById[lid]?.name ?? r.recordLabel?.name ?? lid,
        group:       gruposById[gid]?.name ?? gid,
        public:      r.is_public ? "Sí" : "No",
        genres:      Array.isArray(r.genres) ? r.genres.join(", ") : (r.genres ?? ""),
        recordType:  Array.isArray(r.recordTypes) ? r.recordTypes.join(", ") : (r.recordTypes ?? ""),
        created:     safeDate(r.created_at),
      };
    });
  }, [rows, formatosById, performersById, sellosById, gruposById]);

  // exportar (todo lo filtrado, no solo la página)
  async function fetchAllFiltered() {
    const pageSize = 1000;
    const qs = buildQuery();
    const params = new URLSearchParams(qs);
    let offset = 0;
    let out = [];

    for (;;) {
      params.set("__limit", String(pageSize));
      params.set("__offset", String(offset));
      const { data } = await api.get(`/certified?${params.toString()}`);
      const chunk = unwrapList(data);
      out = out.concat(chunk);
      if (!chunk.length || chunk.length < pageSize) break;
      offset += chunk.length;
    }
    return out;
  }

  const exportAllXLSX = async () => {
    try {
      setLoading(true);
      const all = await fetchAllFiltered();

      const rowsForXlsx = all.map((r) => {
        const fid = String(r.format_id ?? "");
        const pid = String(r.performer_id ?? "");
        const lid = String(r.record_label_id ?? "");
        const gid = String(r.group_id ?? "");
        return {
          artist:      r.artist ?? "",
          title:       r.title ?? "",
          album:       r.album ?? "",
          releaseDate: safeDate(r.release_date),
          isrc:        r.isrc ?? "",
          award:       valueOrName(r.award),
          nationality: r.nationality ?? "",
          units:       formatUnits(r.sale_units),
          format:      formatosById[fid]?.name ?? r.format?.name ?? fid,
          performer:   performersById[pid]?.name ?? r.performer?.name ?? pid,
          label:       sellosById[lid]?.name ?? r.recordLabel?.name ?? lid,
          group:       gruposById[gid]?.name ?? gid,
          public:      r.is_public ? "Sí" : "No",
          genres:      Array.isArray(r.genres) ? r.genres.join(", ") : (r.genres ?? ""),
          recordType:  Array.isArray(r.recordTypes) ? r.recordTypes.join(", ") : (r.recordTypes ?? ""),
          created:     safeDate(r.created_at),
        };
      });

      await exportXLSX({
        filename: `reporte_certificaciones_${Date.now()}.xlsx`,
        cols: COLS,
        rows: rowsForXlsx,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportXLS = async () => {
    await exportXLSX({
      filename: `reporte_certificaciones_${Date.now()}.xlsx`,
      cols: COLS,
      rows: tableRows,
    });
  };

  const isActive = (key) => range === key;
  const onEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setPage(0);
      fetchRows({ pageIndex: 0, pageSize: rowsPerPage });
    }
  };

  const handleSearchClick = () => {
    setPage(0);
    fetchRows({ pageIndex: 0, pageSize: rowsPerPage });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox px={3} py={4}>
        <SoftTypography variant="h5" mb={2}>
          Reportes Certificaciones
        </SoftTypography>

        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(err)}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            {/* rango rápido */}
            <Grid item xs={12}>
              <SoftBox display="flex" gap={1} flexWrap="wrap">
                <SoftButton color={isActive("all") ? "info" : "secondary"} variant={isActive("all") ? "gradient" : "outlined"} onClick={() => setQuickRange("all")}>Todos</SoftButton>
                <SoftButton color={isActive("1m")  ? "info" : "secondary"} variant={isActive("1m")  ? "gradient" : "outlined"} onClick={() => setQuickRange("1m")}>1 mes</SoftButton>
                <SoftButton color={isActive("6m")  ? "info" : "secondary"} variant={isActive("6m")  ? "gradient" : "outlined"} onClick={() => setQuickRange("6m")}>6 meses</SoftButton>
                <SoftButton color={isActive("1y")  ? "info" : "secondary"} variant={isActive("1y")  ? "gradient" : "outlined"} onClick={() => setQuickRange("1y")}>1 año</SoftButton>
              </SoftBox>
            </Grid>

            {/* fechas */}
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                <SoftBox display="flex" gap={2} flexWrap="wrap">
                  <DatePicker label="Desde" value={from} onChange={(v) => setFrom(v)} renderInput={(params) => <TextField {...params} sx={{ width: 170 }} />} />
                  <DatePicker label="Hasta" value={to} onChange={(v) => setTo(v)} renderInput={(params) => <TextField {...params} sx={{ width: 170 }} />} />
                </SoftBox>
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* autocompletes de texto libre */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={optArtist}
                loading={loadingArtist}
                inputValue={artist}
                onInputChange={(_, val) => { setArtist(val); debouncedArtist(val); }}
                onChange={(_, val) => { setArtist(val || ""); fetchRows({ pageIndex: 0, pageSize: rowsPerPage }); }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Artista"
                    onKeyDown={onEnter}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (<>{loadingArtist ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={optTitle}
                loading={loadingTitle}
                inputValue={title}
                onInputChange={(_, val) => { setTitle(val); debouncedTitle(val); }}
                onChange={(_, val) => { setTitle(val || ""); fetchRows({ pageIndex: 0, pageSize: rowsPerPage }); }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Título"
                    onKeyDown={onEnter}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (<>{loadingTitle ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={optAlbum}
                loading={loadingAlbum}
                inputValue={album}
                onInputChange={(_, val) => { setAlbum(val); debouncedAlbum(val); }}
                onChange={(_, val) => { setAlbum(val || ""); fetchRows({ pageIndex: 0, pageSize: rowsPerPage }); }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Álbum"
                    onKeyDown={onEnter}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (<>{loadingAlbum ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                  />
                )}
              />
            </Grid>

            {/* DROPLISTS con scroll + búsqueda */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={formatAC.options}
                loading={formatAC.loading}
                value={formatAC.option}
                onChange={(_, val) => formatAC.setOption(val)}
                inputValue={formatAC.input}
                onInputChange={(_, val) => formatAC.setInput(val)}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Formato"
                    placeholder="Escribe para buscar…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (<>{formatAC.loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                  />
                )}
                ListboxProps={formatAC.listboxProps}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={perfAC.options}
                loading={perfAC.loading}
                value={perfAC.option}
                onChange={(_, val) => perfAC.setOption(val)}
                inputValue={perfAC.input}
                onInputChange={(_, val) => perfAC.setInput(val)}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Intérprete"
                    placeholder="Escribe para buscar…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (<>{perfAC.loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                  />
                )}
                ListboxProps={perfAC.listboxProps}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={labelAC.options}
                loading={labelAC.loading}
                value={labelAC.option}
                onChange={(_, val) => labelAC.setOption(val)}
                inputValue={labelAC.input}
                onInputChange={(_, val) => labelAC.setInput(val)}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sello"
                    placeholder="Escribe para buscar…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (<>{labelAC.loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                  />
                )}
                ListboxProps={labelAC.listboxProps}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                disabled={false}
                options={groupAC.options}
                loading={groupAC.loading}
                value={groupAC.option}
                onChange={(_, val) => groupAC.setOption(val)}
                inputValue={groupAC.input}
                onInputChange={(_, val) => groupAC.setInput(val)}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Grupo"
                    placeholder="Escribe para buscar…"
                    helperText=" "
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (<>{groupAC.loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                  />
                )}
                ListboxProps={groupAC.listboxProps}
              />
            </Grid>

            {/* acciones */}
            <Grid item xs={12}>
              <SoftBox display="flex" gap={1} flexWrap="wrap">
                <Button color="dark" startIcon={<SearchIcon />} variant="contained" onClick={handleSearchClick} disabled={loading}>Buscar</Button>
                <Button color="dark" startIcon={<DownloadIcon />} variant="contained" onClick={exportAllXLSX} disabled={loading}>Exportar Excel</Button>
                <Button color="error" startIcon={<ClearAllIcon />} variant="contained" onClick={clearFilters} disabled={loading}>Borrar filtros</Button>
              </SoftBox>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabla */}
        <Paper variant="outlined" sx={{ p: 1, overflowX: "auto" }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              minWidth: `${TABLE_MIN_WIDTH}px`,
              borderCollapse: "separate",
              borderSpacing: 0,
              tableLayout: "fixed",
              "& th, & td": {
                fontSize: 12,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                padding: "8px 10px",
                borderBottom: "1px solid #eee",
                verticalAlign: "middle",
                boxSizing: "border-box",
              },
              "& thead th": {
                fontWeight: 600,
                position: "sticky",
                top: 0,
                background: (t) => t.palette.background.paper,
                zIndex: 1,
              },
            }}
          >
            <colgroup>
              {COLS.map((c) => (
                <col key={c.key} style={{ width: `${c.w}px` }} />
              ))}
            </colgroup>

            <thead>
              <tr>
                {COLS.map((c) => (
                  <th key={c.key} style={{ textAlign: "left" }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {tableRows.length ? (
                tableRows.map((r, i) => (
                  <tr key={i}>
                    {COLS.map((c) => (
                      <td key={c.key} title={typeof r[c.key] === "string" ? r[c.key] : undefined}>
                        {r[c.key] ?? ""}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={COLS.length} style={{ textAlign: "center", color: "#777", padding: "16px" }}>
                    {loading ? "Buscando…" : "Sin resultados"}
                  </td>
                </tr>
              )}
            </tbody>
          </Box>

          <SoftTypography variant="caption" color="text" sx={{ mt: 1, display: "block" }}>
            Resultados: {tableRows.length}
          </SoftTypography>
        </Paper>

        <SoftBox sx={{ display: "flex", justifyContent: "flex-end", px: 1, overflow: "visible" }}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => {
              setPage(newPage);
              fetchRows({ pageIndex: newPage, pageSize: rowsPerPage });
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setRowsPerPage(newSize);
              setPage(0);
              fetchRows({ pageIndex: 0, pageSize: newSize });
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas:"
            sx={{
              width: "auto",
              overflow: "visible",
              "& .MuiTablePagination-toolbar": {
                minHeight: 40,
                py: 0.25,
                gap: 0.75,
                justifyContent: "flex-end",
                alignItems: "center",
                overflow: "visible",
              },
              "& .MuiTablePagination-spacer": { display: "none" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                m: 0,
                fontSize: "0.8rem",
              },
              "& .MuiTablePagination-input": {
                m: 0,
                ml: 1,
                overflow: "visible",
                "& .MuiInputBase-root": {
                  height: 32,
                  fontSize: "0.8rem",
                  borderRadius: 10,
                  overflow: "visible",
                },
                "& .MuiOutlinedInput-input": { py: 0.5 },
              },
              "& .MuiTablePagination-actions": { ml: 0.5 },
            }}
          />
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}
